// Reads and writes the .sql "book" format used by own-inspector/sql/parcel_queries.sql:
//
//   /* ===== Title =====
//      notes...
//   */
//   BEGIN TRANSACTION READ ONLY;
//   SELECT ...;
//   ROLLBACK;
//
// The READ ONLY wrapper is stripped on import (the app adds it back when copying) and written on export.

export type BookQuery = { title: string; notes: string; sql: string };

const BEGIN = /^\s*BEGIN(\s+TRANSACTION)?\s+READ\s+ONLY\s*;\s*/i;
const END = /\s*ROLLBACK\s*;\s*$/i;

export function unwrap(sql: string): string {
  return sql.trim().replace(BEGIN, "").replace(END, "").trim();
}

export function wrapReadOnly(sql: string): string {
  return `BEGIN TRANSACTION READ ONLY;\n\n${unwrap(sql)}\n\nROLLBACK;\n`;
}

export function parseBook(text: string): BookQuery[] {
  const out: BookQuery[] = [];
  const parts = text.split(/\/\*\s*=====\s*/).slice(1);
  for (const part of parts) {
    const titleEnd = part.indexOf("=====");
    const commentEnd = part.indexOf("*/");
    if (titleEnd < 0 || commentEnd < 0) continue;
    const title = part.slice(0, titleEnd).trim();
    const notes = part
      .slice(titleEnd + 5, commentEnd)
      .split("\n")
      .map((l) => l.replace(/^ {0,3}/, "").trimEnd())
      .join("\n")
      .trim();
    const sql = unwrap(part.slice(commentEnd + 2));
    if (title && sql) out.push({ title, notes, sql });
  }
  return out;
}

export function formatBook(queries: BookQuery[], header = ""): string {
  const blocks = queries.map((q) => {
    const notes = q.notes.trim() ? `\n${q.notes.trim().split("\n").map((l) => `   ${l}`.trimEnd()).join("\n")}\n` : "\n";
    // "*/" inside notes would end the comment early
    return `/* ===== ${q.title.replace(/=====/g, "=")} =====${notes.replace(/\*\//g, "* /")}*/\n${wrapReadOnly(q.sql)}`;
  });
  return (header ? `/*\n${header}\n*/\n\n\n` : "") + blocks.join("\n\n");
}

/* ---------- page meta tags (title, description, OpenGraph, Twitter) for shared URLs ---------- */
export type PageMeta = { title: string; ogTitle: string; description: string; keywords: string };

const oneLine = (s: string) => s.replace(/\s+/g, " ").trim();
const clip = (s: string, max = 200) => {
  const c = oneLine(s);
  return c.length > max ? `${c.slice(0, max - 1).trimEnd()}…` : c;
};
const splitTags = (tags?: string) => (tags ?? "").split(",").map((t) => t.trim()).filter(Boolean);
const escAttr = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

export function queryMeta(q: { title: string; tags?: string; notes?: string; sql?: string }): PageMeta {
  const title = oneLine(q.title) || "Untitled query";
  const tags = splitTags(q.tags);
  const hashTags = tags.map((t) => `#${t}`).join(" ");
  const ogTitle = hashTags ? `${title} · ${hashTags}` : title;
  const notes = clip(q.notes ?? "", 120);
  const sql = clip(unwrap(q.sql ?? "").replace(/\/\*[\s\S]*?\*\/|--[^\n]*/g, " "), 140);
  const description = clip([hashTags, notes, sql].filter(Boolean).join(" · ") || "SQL query in Query Book", 240);
  const keywords = [title, ...tags, "SQL", "Query Book"].join(", ");
  return { title: `${title} · Query Book`, ogTitle, description, keywords };
}

export function reportMeta(r: { title: string; intro?: string; report_date?: string; blocks?: number }): PageMeta {
  const title = oneLine(r.title) || "Untitled report";
  const date = oneLine(r.report_date ?? "");
  const ogTitle = date ? `${title} · ${date}` : title;
  const blocks = r.blocks ? `${r.blocks} block${r.blocks === 1 ? "" : "s"}` : "";
  const intro = clip(r.intro ?? "", 180);
  const description = clip([date, blocks, intro].filter(Boolean).join(" · ") || "Report in Query Book", 240);
  const keywords = [title, date, "report", "Query Book"].filter(Boolean).join(", ");
  return { title: `${title} · Query Book`, ogTitle, description, keywords };
}

export function bookMeta(queries: { title: string; tags?: string }[] = []): PageMeta {
  if (!queries.length) {
    const def = "Keep, search and copy SQL queries, and lay pasted results out as reports.";
    return { title: "Query Book", ogTitle: "Query Book", description: def, keywords: "SQL, queries, reports, Query Book" };
  }
  const counts = new Map<string, number>();
  for (const t of queries.flatMap((q) => splitTags(q.tags))) counts.set(t, (counts.get(t) ?? 0) + 1);
  const topTags = [...counts.keys()].sort((a, b) => counts.get(b)! - counts.get(a)! || a.localeCompare(b)).slice(0, 6);
  const hashTags = topTags.map((t) => `#${t}`).join(" ");
  const sampleTitles = queries.slice(0, 4).map((q) => oneLine(q.title)).filter(Boolean);
  const countStr = `${queries.length} SQL quer${queries.length === 1 ? "y" : "ies"}`;
  const ogTitle = hashTags ? `Query Book · ${hashTags}` : `Query Book (${countStr})`;
  const description = clip([countStr, hashTags, sampleTitles.join(" · ")].filter(Boolean).join(" · "), 240);
  const keywords = [...topTags, ...sampleTitles, "SQL", "Query Book"].join(", ");
  return { title: "Query Book", ogTitle, description, keywords };
}

export function metaHtml(m: PageMeta, url?: string): string {
  return `<title>${escAttr(m.title)}</title>
    <meta name="description" content="${escAttr(m.description)}" />
    <meta name="keywords" content="${escAttr(m.keywords)}" />
    <meta property="og:type" content="website" />
    <meta property="og:site_name" content="Query Book" />
    <meta property="og:title" content="${escAttr(m.ogTitle)}" />
    <meta property="og:description" content="${escAttr(m.description)}" />${url ? `\n    <meta property="og:url" content="${escAttr(url)}" />` : ""}
    <meta name="twitter:card" content="summary" />
    <meta name="twitter:title" content="${escAttr(m.ogTitle)}" />
    <meta name="twitter:description" content="${escAttr(m.description)}" />`;
}

export function injectMeta(html: string, m: PageMeta, url?: string): string {
  return html.replace(/<title>[\s\S]*?<meta name="twitter:description"[^>]*>|<title>[^<]*<\/title>/, metaHtml(m, url));
}

/** Multi-line share text (title, #tags, note/SQL snippet, URL) so private-LAN links still show full context in Telegram/Slack. */
export function shareQueryText(q: { title: string; tags?: string; notes?: string; sql?: string }, url: string): string {
  const title = oneLine(q.title) || "Untitled query";
  const tags = splitTags(q.tags).map((t) => `#${t}`).join(" ");
  const head = tags ? `**${title}** · ${tags}` : `**${title}**`;
  const note = clip(q.notes ?? "", 140);
  const sql = clip(unwrap(q.sql ?? "").replace(/\/\*[\s\S]*?\*\/|--[^\n]*/g, " ").replace(/`/g, "'"), 120);
  return [head, note, sql ? `\`${sql}\`` : "", url].filter(Boolean).join("\n");
}

export function shareReportText(r: { title: string; intro?: string; report_date?: string; blocks?: number }, url: string): string {
  const title = oneLine(r.title) || "Untitled report";
  const meta = [oneLine(r.report_date ?? ""), r.blocks ? `${r.blocks} block${r.blocks === 1 ? "" : "s"}` : ""].filter(Boolean).join(" · ");
  const head = meta ? `**${title}** · ${meta}` : `**${title}**`;
  const intro = clip(r.intro ?? "", 160);
  return [head, intro, url].filter(Boolean).join("\n");
}
